import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';

export interface CurrencyRate {
  code: string;
  name: string;
  flag: string;
  symbol: string;
  rate?: number; // Taxa respecte a EUR (EUR -> X)
}

const LOCALE_MAP: { [lang: string]: string } = {
  ca: 'ca-ES',
  en: 'en-US',
  es: 'es-ES',
  'pt-BR': 'pt-BR'
};

@Component({
  selector: 'app-currency-dialog',
  templateUrl: './currency-dialog.component.html',
  styleUrls: ['./currency-dialog.component.scss']
})
export class CurrencyDialogComponent implements OnInit, OnDestroy {

  currencies: CurrencyRate[] = [];
  isLoading = true;
  hasError = false;
  updatedAt = '';
  inverted = false;         // false = EUR → X  |  true = X → EUR
  selectedCode = 'USD';     // Moneda base quan inverted = true
  
  private refreshInterval: any;
  private unsubscriber = new Subject<void>();
  readonly eurFlagUrl = 'https://flagcdn.com/24x18/eu.png';

  constructor(
    public translate: TranslateService, 
    public languageService: LanguageService,
    private http: HttpClient, 
    public dialogRef: MatDialogRef<CurrencyDialogComponent>
  ) {}

  ngOnInit(): void {
    this.initCurrenciesList();
    this.loadRates();
    this.refreshInterval = setInterval(() => this.loadRates(), 60000);

    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => { this.initCurrenciesList(); });
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  initCurrenciesList(): void {
    const previous = this.currencies;
    const definitions: Array<{ code: string, key: string, flag: string, symbol: string }> = [
      { code: 'EUR', key: 'currencyNames.eur', flag: 'EU', symbol: '€' },
      { code: 'USD', key: 'currencyNames.usd', flag: 'US', symbol: '$' },
      { code: 'BRL', key: 'currencyNames.brl', flag: 'BR', symbol: 'R$' },
      { code: 'COP', key: 'currencyNames.cop', flag: 'CO', symbol: 'COL$' },
      { code: 'GBP', key: 'currencyNames.gbp', flag: 'GB', symbol: '£' },
      { code: 'CHF', key: 'currencyNames.chf', flag: 'CH', symbol: 'CHF' },
      { code: 'MAD', key: 'currencyNames.mad', flag: 'MA', symbol: 'د.م.' },
    ];

    this.currencies = definitions.map(def => {
      const prev = previous.find(c => c.code === def.code);
      return {
        code: def.code,
        name: this.translate.instant(def.key),
        flag: def.flag.toLowerCase(),
        symbol: def.symbol,
        rate: prev?.rate
      };
    });
  }
  
  get visibleCurrencies(): CurrencyRate[] {
    const currentBase = this.inverted ? this.selectedCode : 'EUR';
    return this.currencies.filter(c => c.code !== currentBase);
  }

  loadRates(): void {
    this.isLoading = true;
    this.hasError = false;

    // IMPORTANT: Substituiu YOUR_API_KEY per la vostra clau real d'ExchangeRate-API
    // O podeu apuntar a un endpoint propi del vostre backend Go si voleu amagar la clau.
    const url = `https://v6.exchangerate-api.com/v6/dc6c057c77d8c78e8327af0f/latest/EUR`;

    this.http.get<any>(url).subscribe({
      next: (data) => {
        const rates = data.conversion_rates || {};
        this.currencies = this.currencies.map(cur => ({
          ...cur,
          rate: rates[cur.code] ?? null
        }));

        const now = new Date();
        const locale = LOCALE_MAP[this.translate.currentLang] || 'en-US';
        this.updatedAt = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      }
    });
  }

  // Intercanvia la direcció (EUR -> X  <-->  X -> EUR)
  toggleDirection(): void {
    this.inverted = !this.inverted;
  }

  // Quan l'usuari clica a una moneda per posar-la a l'esquerra (mode invertit)
  selectCurrency(code: string): void {
    this.selectedCode = code;
    this.inverted = true;
  }

  get selected(): CurrencyRate {
    return this.currencies.find(c => c.code === this.selectedCode) ?? this.currencies[0];
  }

  get selectedRateToEur(): number {
    // Quant val 1 unitat de la moneda seleccionada en EUR (1 / rateEUR->X)
    const rate = this.selected?.rate;
    return rate ? 1 / rate : 0;
  }

  // Calcula el valor final a mostrar per a cada fila segons el sentit actual
  getDisplayRate(cur: CurrencyRate): string {
    if (!cur.rate || !this.selected.rate) return '-';

    if (!this.inverted) {
      // Sentit EUR -> X
      return cur.rate.toFixed(4);
    } else {
      // Sentit X -> Altres (o X -> EUR)
      if (cur.code === this.selected.code) return '1.0000';
      // Fórmula: (1 / rateEUR->Selected) * rateEUR->Cur
      const val = this.selectedRateToEur * cur.rate;
      return val.toFixed(4);
    }
  }

  flagUrl(flag: string): string {
    return `https://flagcdn.com/24x18/${flag}.png`;
  }

  close(): void {
    this.dialogRef.close();
  }
}